import { useWidget } from '../../../providers/widget'
import { CalendarWidgetConfig } from '../config'
import Stylesheet from '../../../components/Stylesheet'
import { useEffect, useMemo, useState } from 'react'
import CalendarGrid from './CalendarGrid'
import WebFont from '../../../components/WebFont'
import WidgetSection from '../../../components/WidgetSection'
import { useFetchProperty } from '../data'
import { resolveWidgetSpacingStyle } from "@homerunner-next/widget-core/spacing"
import { hostFontSizeCss } from '../../../common/host-scope'

/**
 * Dark-palette overrides for the auto FIRST-PAINT path (#900). The inline
 * template below always emits the LIGHT (or resolved) palette so light output
 * stays byte-identical; this appends the DARK palette — the `--hr_cal-*`
 * variables, the accent utilities, and the container color flip — under two
 * selector prefixes:
 *   - `:host([data-hr-theme="dark"])` — the runtime marker (explicit dark +
 *     post-hydration auto), and the mirrored elevated host.
 *   - inside `@media (prefers-color-scheme: dark)`,
 *     `:host([data-hr-scheme="auto"]:not([data-hr-theme]))` — a server-stamped
 *     auto widget on a dark OS, painting dark AT PARSE with no JS/cookie; the
 *     `:not([data-hr-theme])` (kept INSIDE `:host(...)`) drops it the instant
 *     the runtime stamps the marker at hydration.
 * Values come from the DARK color set DIRECTLY (not `resolvedTheme`-gated), so
 * the override ships on every render. `:host(...)` (not a descendant
 * `[data-hr-theme]`) is required to reach the shadow host from its own sheet.
 */
function calendarDarkOverrides(
  prefix: string,
  dc: CalendarWidgetConfig['darkModeColors'],
): string {
  const container = `${prefix} .homerunner-calendar-container`
  return `
    ${container} {
      color: var(--foreground) !important;
    }
    ${dc.accent ? `
    ${prefix} .text-accent {
      color: ${dc.accent} !important;
    }
    ${prefix} .bg-accent {
      background-color: ${dc.accent} !important;
    }
    ${prefix} .border-accent {
      border-color: ${dc.accent} !important;
    }
    ` : ''}
    ${container} {
      /* Accent (themes the clear-dates button etc.) */
      --hr_cal-accent: ${dc.accent};

      /* Selectable */
      --hr_cal-color_selectable: ${dc.selectableColor};
      --hr_cal-bg_selectable: ${dc.selectableBackground};
      --hr_cal-price_color_selectable: ${dc.selectablePriceColor};

      /* Passthrough */
      --hr_cal-color_pass-through: ${dc.passthroughColor};
      --hr_cal-bg_pass-through: ${dc.passthroughBackground};
      --hr_cal-price_color_pass-through: ${dc.passthroughPriceColor};

      /* Selected */
      --hr_cal-color_selected: ${dc.selectedColor};
      --hr_cal-bg_selected: ${dc.selectedBackground};
      --hr_cal-price_color_selected: ${dc.selectedPriceColor};

      /* Blocked */
      --hr_cal-color_blocked: ${dc.blockedColor};
      --hr_cal-bg_blocked: ${dc.blockedBackground};
      --hr_cal-color_checkin-blocked: ${dc.blockedColor};
      --hr_cal-bg_checkin-blocked: ${dc.blockedBackground};
      --hr_cal-price_color_checkin-blocked: ${dc.blockedColor};
      --hr_cal-color_checkout-blocked: ${dc.blockedColor};
      --hr_cal-bg_checkout-blocked: ${dc.blockedBackground};
      --hr_cal-price_color_checkout-blocked: ${dc.blockedColor};

      /* Booked */
      --hr_cal-color_booked: ${dc.bookedColor};
      --hr_cal-bg_booked: ${dc.bookedBackground};
      --hr_cal-color_checkin-booked: ${dc.bookedColor};
      --hr_cal-bg_checkin-booked: ${dc.bookedBackground};
      --hr_cal-price_color_checkin-booked: ${dc.bookedColor};
      --hr_cal-color_checkout-booked: ${dc.bookedColor};
      --hr_cal-bg_checkout-booked: ${dc.bookedBackground};
      --hr_cal-price_color_checkout-booked: ${dc.bookedColor};

      /* Disabled */
      --hr_cal-color_disabled: ${dc.disabledColor};
      --hr_cal-bg_disabled: ${dc.disabledBackground};
      --hr_cal-price_color_disabled: ${dc.disabledColor};
      --hr_cal-color_middle-disabled: ${dc.disabledColor};
      --hr_cal-bg_middle-disabled: ${dc.disabledBackground};
      --hr_cal-price_color_middle-disabled: ${dc.disabledColor};

      /* Unavailable */
      --hr_cal-color_unavailable: ${dc.unavailableColor};
      --hr_cal-bg_unavailable: ${dc.unavailableBackground};
      --hr_cal-price_color_unavailable: ${dc.unavailableColor};

      /* Trapped */
      --hr_cal-color_trapped: ${dc.unavailableColor};
      --hr_cal-bg_trapped: ${dc.unavailableBackground};
      --hr_cal-price_color_trapped: ${dc.unavailableColor};

      /* Checkin available */
      --hr_cal-color_checkin-available: ${dc.selectableColor};
      --hr_cal-price_color_checkin-available: ${dc.selectablePriceColor};

      /* Checkout available */
      --hr_cal-color_checkout-available: ${dc.bookedColor};
      --hr_cal-price_color_checkout-available: ${dc.bookedColor};

      /* Checkin/Checkout selection */
      --hr_cal-bg_checkin: ${dc.selectedBackground};
      --hr_cal-color_checkin: ${dc.selectedColor};
      --hr_cal-price_color_checkin: ${dc.selectedPriceColor};
      --hr_cal-bg_checkout: ${dc.selectedBackground};
      --hr_cal-color_checkout: ${dc.selectedColor};
      --hr_cal-price_color_checkout: ${dc.selectedPriceColor};

      /* Hovered */
      --hr_cal-bg_hovered: ${dc.selectedBackground};
      --hr_cal-color_hovered: ${dc.selectedColor};
      --hr_cal-price_color_hovered: ${dc.selectedPriceColor};
    }
  `
}

export function CalendarWidgetContent() {
  const { options, resolvedTheme, widgetId } = useWidget<CalendarWidgetConfig>()

  const colors = useMemo(() => {
    return resolvedTheme === 'dark' ? options.darkModeColors : options.lightModeColors
  }, [resolvedTheme, options.lightModeColors, options.darkModeColors])

  // Section title only renders once we know the property loaded successfully.
  // React Query dedupes with the call inside <CalendarGrid />.
  const { data: propertyData } = useFetchProperty(options.filter)
  const hasContent = Boolean(propertyData?.result)

  // Modal display mode (WP AvailabilityCalendar type=modal parity).
  // Trigger elements live in the host page's light DOM, so listen on document
  // with delegation — this also covers triggers added after mount.
  const isModal = options.displayMode === 'modal'
  const [modalOpen, setModalOpen] = useState(false)
  useEffect(() => {
    if (!isModal || !options.modalTriggerSelector) return
    const selector = options.modalTriggerSelector
    const handler = (e: MouseEvent) => {
      const target = e.target as Element | null
      if (target?.closest?.(selector)) {
        e.preventDefault()
        setModalOpen(true)
      }
    }
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [isModal, options.modalTriggerSelector])

  const cssVariables = useMemo(() => `
    .homerunner-calendar-container {
      /* Accent (themes the clear-dates button etc.) */
      --hr_cal-accent: ${colors.accent};

      /* Selectable */
      --hr_cal-color_selectable: ${colors.selectableColor};
      --hr_cal-bg_selectable: ${colors.selectableBackground};
      --hr_cal-price_color_selectable: ${colors.selectablePriceColor};

      /* Passthrough */
      --hr_cal-color_pass-through: ${colors.passthroughColor};
      --hr_cal-bg_pass-through: ${colors.passthroughBackground};
      --hr_cal-price_color_pass-through: ${colors.passthroughPriceColor};

      /* Selected */
      --hr_cal-color_selected: ${colors.selectedColor};
      --hr_cal-bg_selected: ${colors.selectedBackground};
      --hr_cal-price_color_selected: ${colors.selectedPriceColor};

      /* Blocked */
      --hr_cal-color_blocked: ${colors.blockedColor};
      --hr_cal-bg_blocked: ${colors.blockedBackground};
      --hr_cal-color_checkin-blocked: ${colors.blockedColor};
      --hr_cal-bg_checkin-blocked: ${colors.blockedBackground};
      --hr_cal-price_color_checkin-blocked: ${colors.blockedColor};
      --hr_cal-color_checkout-blocked: ${colors.blockedColor};
      --hr_cal-bg_checkout-blocked: ${colors.blockedBackground};
      --hr_cal-price_color_checkout-blocked: ${colors.blockedColor};

      /* Booked */
      --hr_cal-color_booked: ${colors.bookedColor};
      --hr_cal-bg_booked: ${colors.bookedBackground};
      --hr_cal-color_checkin-booked: ${colors.bookedColor};
      --hr_cal-bg_checkin-booked: ${colors.bookedBackground};
      --hr_cal-price_color_checkin-booked: ${colors.bookedColor};
      --hr_cal-color_checkout-booked: ${colors.bookedColor};
      --hr_cal-bg_checkout-booked: ${colors.bookedBackground};
      --hr_cal-price_color_checkout-booked: ${colors.bookedColor};

      /* Disabled */
      --hr_cal-color_disabled: ${colors.disabledColor};
      --hr_cal-bg_disabled: ${colors.disabledBackground};
      --hr_cal-price_color_disabled: ${colors.disabledColor};
      --hr_cal-color_middle-disabled: ${colors.disabledColor};
      --hr_cal-bg_middle-disabled: ${colors.disabledBackground};
      --hr_cal-price_color_middle-disabled: ${colors.disabledColor};

      /* Unavailable */
      --hr_cal-color_unavailable: ${colors.unavailableColor};
      --hr_cal-bg_unavailable: ${colors.unavailableBackground};
      --hr_cal-price_color_unavailable: ${colors.unavailableColor};

      /* Trapped */
      --hr_cal-color_trapped: ${colors.unavailableColor};
      --hr_cal-bg_trapped: ${colors.unavailableBackground};
      --hr_cal-price_color_trapped: ${colors.unavailableColor};

      /* Checkin available */
      --hr_cal-color_checkin-available: ${colors.selectableColor};
      --hr_cal-price_color_checkin-available: ${colors.selectablePriceColor};

      /* Checkout available */
      --hr_cal-color_checkout-available: ${colors.bookedColor};
      --hr_cal-price_color_checkout-available: ${colors.bookedColor};

      /* Checkin/Checkout selection */
      --hr_cal-bg_checkin: ${colors.selectedBackground};
      --hr_cal-color_checkin: ${colors.selectedColor};
      --hr_cal-price_color_checkin: ${colors.selectedPriceColor};
      --hr_cal-bg_checkout: ${colors.selectedBackground};
      --hr_cal-color_checkout: ${colors.selectedColor};
      --hr_cal-price_color_checkout: ${colors.selectedPriceColor};

      /* Hovered */
      --hr_cal-bg_hovered: ${colors.selectedBackground};
      --hr_cal-color_hovered: ${colors.selectedColor};
      --hr_cal-price_color_hovered: ${colors.selectedPriceColor};
    }
  `, [colors])

  return (
    <div
      className={`homerunner-calendar-container homerunner-calendar-app w-full ${resolvedTheme === 'dark' ? 'dark' : ''}`}
      style={resolveWidgetSpacingStyle(options)}
      suppressHydrationWarning
    >
      <WebFont />
      <Stylesheet
        css={`
          .homerunner-calendar-container *:not([class^=icon-]) {
            font-family: '${options.font.family}' !important;
          }
          .homerunner-calendar-container.dark {
            color: var(--foreground) !important;
          }
          ${hostFontSizeCss(widgetId, options.font.size)}
          ${colors.accent ? `
          .text-accent {
            color: ${colors.accent} !important;
          }
          .bg-accent {
            background-color: ${colors.accent} !important;
          }
          .border-accent {
            border-color: ${colors.accent} !important;
          }
          ` : ''}
          ${cssVariables}
          /* Dark palette for the auto first-paint path (#900) plus the runtime
             marker. The rules above always emit LIGHT (or the resolved theme);
             these override only under the dark host markers, so light rendering
             stays byte-identical. */
          ${calendarDarkOverrides(':where(:host([data-hr-theme="dark"]))', options.darkModeColors)}
          @media (prefers-color-scheme: dark) {
            ${calendarDarkOverrides(':where(:host([data-hr-scheme="auto"]:not([data-hr-theme])))', options.darkModeColors)}
          }
          ${options.customCss ?? ''}
        `}
      />
      {isModal ? (
        modalOpen && (
          <div
            className="hr-calendar-modal-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false) }}
          >
            <div className="hr-calendar-modal" role="dialog" aria-modal="true">
              <button
                type="button"
                className="hr-calendar-modal-close"
                onClick={() => setModalOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
              {hasContent && (
                <WidgetSection show={options.section?.show} title={options.section?.title} forceHeadingInMarkup />
              )}
              <CalendarGrid />
            </div>
          </div>
        )
      ) : (
        <>
          {hasContent && (
            <WidgetSection show={options.section?.show} title={options.section?.title} forceHeadingInMarkup />
          )}
          <CalendarGrid />
        </>
      )}
    </div>
  )
}
